/**
 * @fileoverview Advanced data-binding editor — links inspection form fields (the field catalog)
 * to a document template and lets the user copy `{{advField:id}}` tokens. Shared by both builders.
 * @module ui-admin/template-builder/documentSectionEditors/AdvancedDataEditor
 *
 * Responsibilities: - Browse/search the field catalog grouped by form; add/remove field placements.
 *                   - Provide copy-token affordances and the "render as section" options.
 * Dependencies:     - @firesquirrel/core-domain (FieldCatalog, FieldCatalogEntry, AdvancedFieldPlacement);
 *                     lucide-react icons.
 * Architecture:     - Presentation-only, fully controlled via props. Extracted from the tenant builder
 *                     page (G-DRY-01) so system-web can render the identical editor.
 * Notes:            - Holds only local UI state (search text, expanded form); all data via props.
 */

import { useState } from 'react';
import { ClipboardList, Check, Plus, Trash2 } from 'lucide-react';
import type { FieldCatalog, FieldCatalogEntry, AdvancedFieldPlacement } from '@firesquirrel/core-domain';

/**
 * @component AdvancedDataEditor
 * Purpose:     Bind inspection form fields to a document template and manage their placements.
 * Props:       fieldCatalog, advancedFields, onAddField, onRemoveField, linkedTemplateName?,
 *              renderAdvancedSection, onRenderAdvancedSectionChange, advancedSectionLabel,
 *              onAdvancedSectionLabelChange, copiedFieldId, onCopyToken, isViewMode.
 * Behavior:    Controlled; local-only search/expand state; all mutations via callbacks.
 * Data Source: local (catalog + placements supplied by the page from Dexie-backed services).
 * Constraints: presentation-only; no API calls.
 * Performance: catalog list scrolls within a fixed max-height container.
 */
export function AdvancedDataEditor({
  fieldCatalog,
  advancedFields,
  onAddField,
  onRemoveField,
  linkedTemplateName,
  renderAdvancedSection,
  onRenderAdvancedSectionChange,
  advancedSectionLabel,
  onAdvancedSectionLabelChange,
  copiedFieldId,
  onCopyToken,
  isViewMode,
}: {
  fieldCatalog: FieldCatalog | null;
  advancedFields: AdvancedFieldPlacement[];
  onAddField: (entry: FieldCatalogEntry) => void;
  onRemoveField: (id: string) => void;
  linkedTemplateName?: string;
  renderAdvancedSection: boolean;
  onRenderAdvancedSectionChange: (v: boolean) => void;
  advancedSectionLabel: string;
  onAdvancedSectionLabelChange: (v: string) => void;
  copiedFieldId: string | null;
  onCopyToken: (fieldId: string) => void;
  isViewMode: boolean;
}) {
  const [search, setSearch] = useState('');
  const [expandedForm, setExpandedForm] = useState<string | null>(null);

  const addedFieldIds = new Set(advancedFields.map(f => f.fieldId));

  const formGroups = fieldCatalog
    ? fieldCatalog.entries.reduce<Record<string, FieldCatalogEntry[]>>((acc, entry) => {
        const key = entry.formId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(entry);
        return acc;
      }, {})
    : {};

  const filteredEntries = fieldCatalog
    ? fieldCatalog.entries.filter(e =>
        e.label.toLowerCase().includes(search.toLowerCase()) ||
        e.formName.toLowerCase().includes(search.toLowerCase()) ||
        e.fieldId.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-medium text-gray-900">Advanced Data Bindings</div>
        <p className="text-xs text-gray-500 mt-1">
          Link inspection form fields to this document template.
          {linkedTemplateName && <> Linked to: <span className="font-medium text-blue-600">{linkedTemplateName}</span></>}
        </p>
      </div>

      {!fieldCatalog ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <ClipboardList className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Select an inspection template to see available fields</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {/* Left: Field Catalog */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
              <div className="text-xs font-semibold text-gray-700 mb-1.5">Available Fields</div>
              <input
                type="text"
                placeholder="Search fields..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              />
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {search ? (
                <div className="p-2 space-y-1">
                  {filteredEntries.map(entry => (
                    <div key={entry.fieldId} className="flex items-center justify-between px-2 py-1.5 rounded-sm hover:bg-gray-50">
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">{entry.label}</div>
                        <div className="text-[10px] text-gray-400">{entry.formName} · {entry.type}</div>
                      </div>
                      <button
                        onClick={() => onAddField(entry)}
                        disabled={addedFieldIds.has(entry.fieldId)}
                        className={`shrink-0 p-1 rounded ${
                          addedFieldIds.has(entry.fieldId)
                            ? 'text-green-400 cursor-default'
                            : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                        }`}
                      >
                        {addedFieldIds.has(entry.fieldId) ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  ))}
                  {filteredEntries.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-4">No fields match</div>
                  )}
                </div>
              ) : (
                Object.entries(formGroups).map(([formId, entries]) => {
                  const formName = entries[0]?.formName || formId;
                  const isExpanded = expandedForm === formId;
                  return (
                    <div key={formId}>
                      <button
                        onClick={() => setExpandedForm(isExpanded ? null : formId)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100"
                      >
                        <span className="text-xs font-medium text-gray-700">{formName}</span>
                        <span className="text-[10px] text-gray-400">{entries.length} fields</span>
                      </button>
                      {isExpanded && (
                        <div className="p-2 space-y-1 bg-white">
                          {entries.map(entry => (
                            <div key={entry.fieldId} className="flex items-center justify-between px-2 py-1.5 rounded-sm hover:bg-gray-50">
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-gray-800 truncate">{entry.label}</div>
                                <div className="text-[10px] text-gray-400">{entry.type}</div>
                              </div>
                              <button
                                onClick={() => onAddField(entry)}
                                disabled={addedFieldIds.has(entry.fieldId)}
                                className={`shrink-0 p-1 rounded ${
                                  addedFieldIds.has(entry.fieldId)
                                    ? 'text-green-400 cursor-default'
                                    : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                                }`}
                              >
                                {addedFieldIds.has(entry.fieldId) ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Current Placements */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
              <div className="text-xs font-semibold text-gray-700">
                Field Placements ({advancedFields.length})
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {advancedFields.length === 0 ? (
                <div className="p-6 text-center">
                  <Plus className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">Add fields from the catalog</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {advancedFields.map((field) => {
                    const token = `{{advField:${field.id}}}`;
                    const isCopied = copiedFieldId === field.id;
                    return (
                      <div key={field.id} className="px-3 py-2 rounded-lg border border-gray-100 bg-white space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-gray-800 truncate">{field.label}</div>
                            <div className="text-[10px] text-gray-400 truncate">{field.formName} · {field.renderMode}</div>
                          </div>
                          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            field.renderMode === 'checkbox' ? 'bg-purple-50 text-purple-600'
                              : field.renderMode === 'photo' ? 'bg-blue-50 text-blue-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {field.renderMode}
                          </span>
                          <button
                            onClick={() => onRemoveField(field.id)}
                            className="shrink-0 p-1 rounded-sm text-gray-400 hover:text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {/* Copy-token row */}
                        <div className="flex items-center gap-2 pt-0.5">
                          <code className="flex-1 text-[10px] bg-blue-50 text-blue-700 px-2 py-1 rounded-sm font-mono truncate border border-blue-100">
                            {token}
                          </code>
                          <button
                            onClick={() => onCopyToken(field.id)}
                            className={`shrink-0 px-2 py-1 text-[10px] font-medium rounded border transition-colors ${
                              isCopied
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {isCopied ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                        <p className="text-[9px] text-gray-400">Paste into a Body Text HTML paragraph to embed this value inline</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {advancedFields.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <Check className="h-3 w-3 text-green-500" />
                  {advancedFields.length} binding{advancedFields.length !== 1 ? 's' : ''} configured
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rendering Options */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="text-xs font-semibold text-gray-700">Rendering Options</div>

        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Section heading label</label>
          <input
            type="text"
            value={advancedSectionLabel}
            onChange={(e) => onAdvancedSectionLabelChange(e.target.value)}
            placeholder="Inspection Data"
            disabled={isViewMode}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          />
          <p className="text-[10px] text-gray-400 mt-1">Shown as the heading above the linked fields table in the PDF.</p>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={renderAdvancedSection}
            onChange={(e) => onRenderAdvancedSectionChange(e.target.checked)}
            disabled={isViewMode}
            className="h-4 w-4 mt-0.5 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500"
          />
          <div>
            <span className="text-sm text-gray-700">Show linked fields as a separate section at the bottom of the PDF</span>
            <p className="text-[10px] text-gray-400 mt-0.5">
              Uncheck this when embedding field values directly in Body Text using{' '}
              <code className="bg-gray-100 px-1 rounded-sm">{'{{advField:fieldId}}'}</code> tokens — copy tokens above and paste into a Body Text HTML paragraph.
            </p>
          </div>
        </label>
      </div>
    </div>
  );
}
